import { useState, useEffect } from 'react';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import './Screen.css';

interface AppSettings {
  autoSync: boolean;
  syncInterval: number;
  cacheSize: number;
  notifications: boolean;
  theme: 'light' | 'dark' | 'auto';
}

export default function Settings() {
  const [settings, setSettings] = useState<AppSettings>({
    autoSync: true,
    syncInterval: 30,
    cacheSize: 100,
    notifications: true,
    theme: 'light',
  });
  const [saved, setSaved] = useState(false);
  const isOnline = useNetworkStatus();

  useEffect(() => {
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleSave = () => {
    localStorage.setItem('appSettings', JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleReset = () => {
    const defaultSettings: AppSettings = {
      autoSync: true,
      syncInterval: 30,
      cacheSize: 100,
      notifications: true,
      theme: 'light',
    };
    setSettings(defaultSettings);
    localStorage.setItem('appSettings', JSON.stringify(defaultSettings));
  };

  const handleClearCache = async () => {
    if (window.confirm('Are you sure you want to clear all cached data?')) {
      try {
        // Clear IndexedDB (used by Amplify DataStore and local data service)
        if ('indexedDB' in window) {
          // Clear all IndexedDB databases
          const databases = await indexedDB.databases();
          await Promise.all(
            databases.map(db => {
              return new Promise<void>((resolve, reject) => {
                const deleteReq = indexedDB.deleteDatabase(db.name || '');
                deleteReq.onsuccess = () => resolve();
                deleteReq.onerror = () => reject(deleteReq.error);
                deleteReq.onblocked = () => resolve(); // Ignore blocked
              });
            })
          );
        }
        
        // Clear localStorage
        localStorage.clear();
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        alert('Cache cleared successfully. Please refresh the page.');
        window.location.reload();
      } catch (error) {
        console.error('Failed to clear cache:', error);
        alert('Failed to clear cache');
      }
    }
  };

  return (
    <div className="screen">
      <div className="screen-header">
        <h1>Settings</h1>
        <p className="screen-subtitle">Configure application settings</p>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <h2>Sync Settings</h2>
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.autoSync}
                onChange={(e) => setSettings({ ...settings, autoSync: e.target.checked })}
              />
              <span>Enable Auto Sync</span>
            </label>
            <p className="setting-description">
              Automatically sync data when online
            </p>
          </div>
          <div className="setting-item">
            <label>
              Sync Interval (seconds)
              <input
                type="number"
                min="10"
                max="300"
                value={settings.syncInterval}
                onChange={(e) => setSettings({ ...settings, syncInterval: parseInt(e.target.value) })}
                className="setting-input"
              />
            </label>
            <p className="setting-description">
              How often to check for sync (10-300 seconds)
            </p>
          </div>
        </div>

        <div className="settings-section">
          <h2>Cache Settings</h2>
          <div className="setting-item">
            <label>
              Cache Size (MB)
              <input
                type="number"
                min="10"
                max="500"
                value={settings.cacheSize}
                onChange={(e) => setSettings({ ...settings, cacheSize: parseInt(e.target.value) })}
                className="setting-input"
              />
            </label>
            <p className="setting-description">
              Maximum cache size in megabytes
            </p>
          </div>
          <div className="setting-item">
            <button className="btn btn-warning" onClick={handleClearCache}>
              Clear Cache
            </button>
            <p className="setting-description">
              Remove all cached data from local storage
            </p>
          </div>
        </div>

        <div className="settings-section">
          <h2>Appearance</h2>
          <div className="setting-item">
            <label>
              Theme
              <select
                value={settings.theme}
                onChange={(e) => setSettings({ ...settings, theme: e.target.value as any })}
                className="setting-input"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="auto">Auto</option>
              </select>
            </label>
            <p className="setting-description">
              Choose your preferred theme
            </p>
          </div>
        </div>

        <div className="settings-section">
          <h2>Notifications</h2>
          <div className="setting-item">
            <label>
              <input
                type="checkbox"
                checked={settings.notifications}
                onChange={(e) => setSettings({ ...settings, notifications: e.target.checked })}
              />
              <span>Enable Notifications</span>
            </label>
            <p className="setting-description">
              Receive notifications for sync status and errors
            </p>
          </div>
        </div>

        <div className="settings-section">
          <h2>System Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Connection Status:</span>
              <span className="info-value">{isOnline ? 'Online' : 'Offline'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">User Agent:</span>
              <span className="info-value">{navigator.userAgent.substring(0, 50)}...</span>
            </div>
            <div className="info-item">
              <span className="info-label">Platform:</span>
              <span className="info-value">{navigator.platform}</span>
            </div>
            <div className="info-item">
              <span className="info-label">App Version:</span>
              <span className="info-value">1.0.0</span>
            </div>
          </div>
        </div>

        <div className="settings-actions">
          <button className="btn btn-primary" onClick={handleSave}>
            {saved ? '✓ Saved' : 'Save Settings'}
          </button>
          <button className="btn btn-secondary" onClick={handleReset}>
            Reset to Defaults
          </button>
        </div>
      </div>
    </div>
  );
}

